import React from "react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import heroImage from "../assets/Hero.png";
import { Heart, Star, Sparkles, Shirt, Globe, Leaf } from "lucide-react";
import { motion } from "framer-motion";

export default function About() {
  return (
    <div
      className="min-h-screen text-gray-800 bg-gradient-to-b from-gray-50 to-gray-100 font-['Roboto',sans-serif]"
    >
      <Navbar />

      {/* 🌟 Hero Section */}
      <section
        className="relative h-[70vh] flex flex-col items-center justify-center text-center overflow-hidden"
        style={{
          backgroundImage: `url(${heroImage})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-b from-gray-900/70 to-gray-700/30"></div>

        <motion.div
          className="relative z-10 max-w-3xl px-4"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.2, ease: "easeOut" }}
        >
          <h1 className="text-6xl md:text-7xl font-extrabold text-white tracking-tight drop-shadow-lg mb-4">
            About Us
          </h1>
          <p className="text-xl md:text-2xl text-white/90 leading-relaxed font-light">
            Redefining fashion through creativity, comfort, and confidence.
          </p>
        </motion.div>
      </section>

      {/* 🧵 Story Section */}
      <section className="py-20 px-6">
        <div className="max-w-6xl mx-auto text-center">
          <motion.h2
            className="text-4xl font-bold text-gray-800 mb-6"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            Our Story
          </motion.h2>
          <motion.p
            className="text-lg text-gray-600 leading-relaxed max-w-3xl mx-auto"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ delay: 0.2, duration: 0.8 }}
          >
            Welcome to <strong>DressShop</strong> — where fashion meets
            individuality. Our journey began with a simple belief: clothing
            should not only look good but also make you feel unstoppable. From
            timeless classics to modern trends, we handpick each piece with
            craftsmanship and comfort in mind.
          </motion.p>
        </div>
      </section>

      {/* 💎 What We Offer */}
      <section className="bg-white py-20 px-6 shadow-inner">
        <div className="max-w-6xl mx-auto text-center">
          <motion.h2
            className="text-4xl font-bold text-gray-800 mb-10"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            What We Offer
          </motion.h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
            {[
              {
                icon: <Shirt className="w-12 h-12 text-gray-600 mx-auto mb-4" />,
                title: "Stylish Apparel",
                desc: "Curated clothing that blends comfort with timeless style — perfect for any occasion.",
              },
              {
                icon: <Star className="w-12 h-12 text-gray-500 mx-auto mb-4" />,
                title: "Premium Quality",
                desc: "We believe in quality that lasts. Every product reflects craftsmanship and care.",
              },
              {
                icon: (
                  <Sparkles className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                ),
                title: "Modern Trends",
                desc: "Stay ahead of fashion with our handpicked designs inspired by the latest trends.",
              },
            ].map((item, index) => (
              <motion.div
                key={index}
                className="bg-gray-50 p-8 rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-500 border border-gray-200"
                whileHover={{ scale: 1.05, y: -5 }}
              >
                {item.icon}
                <h3 className="text-xl font-semibold text-gray-800 mb-2">
                  {item.title}
                </h3>
                <p className="text-gray-600">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* 🌍 Our Mission */}
      <section className="py-20 px-6">
        <div className="max-w-5xl mx-auto text-center">
          <motion.h2
            className="text-4xl font-bold text-gray-800 mb-8"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            Our Mission
          </motion.h2>

          <motion.p
            className="text-lg text-gray-600 leading-relaxed max-w-3xl mx-auto mb-12"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ delay: 0.3, duration: 0.8 }}
          >
            We strive to inspire confidence through fashion. Our mission is to
            craft styles that empower individuals to express themselves — with
            sustainability, elegance, and authenticity.
          </motion.p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
            {[
              {
                icon: (
                  <Heart className="w-10 h-10 text-gray-500 mx-auto mb-4" />
                ),
                label: "Empower People",
              },
              {
                icon: <Leaf className="w-10 h-10 text-gray-600 mx-auto mb-4" />,
                label: "Sustainable Future",
              },
              {
                icon: <Globe className="w-10 h-10 text-gray-700 mx-auto mb-4" />,
                label: "Global Impact",
              },
            ].map((item, i) => (
              <motion.div
                key={i}
                className="bg-white p-8 rounded-xl shadow-md hover:shadow-2xl transition-all duration-500 border border-gray-200"
                whileHover={{ scale: 1.08, rotateY: 5 }}
              >
                {item.icon}
                <h4 className="text-lg font-semibold text-gray-800">
                  {item.label}
                </h4>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

    </div>
  );
}
